/**
 * Hotel Policy Rules API
 *
 * Handles hotel policy rules API calls.
 */
import type { HotelRulesData, HotelPolicyRulesCBPayload } from '@contracts';
import type { HotelPolicyRulesRequestParams } from '../../types/bookingReviewTypes';
import { HOTELS_ENDPOINTS } from '@/api/endpoints';
import { logger } from '@/shared/utils/logger';
import { baseFetch, createApiError, buildCBUrlParamsFull, buildCBHeaders } from './baseApi';

// ============================================
// Error Class
// ============================================

export const HotelPolicyRulesError = createApiError('HotelPolicyRulesError');

// ============================================
// Response Type
// ============================================

export interface HotelPolicyRulesResponse {
  type: 'hotels-policy-rules';
  data: HotelRulesData;
  success?: boolean;
  error?: { code: string; message: string };
}

// ============================================
// Raw API Response Type
// ============================================

interface RawResponse {
  success?: boolean;
  allRules?: unknown[];
  error?: { code: string; message: string };
}

// ============================================
// Helper Functions
// ============================================

/**
 * Build the CB backend payload with urlParams, headers, and requestBody
 * Uses common builders from baseApi
 */
export function buildCBPayload(txnKey: string): HotelPolicyRulesCBPayload {
  return {
    urlParams: buildCBUrlParamsFull(),
    headers: buildCBHeaders(),
    requestBody: { txnKey },
  };
}

// ============================================
// API Function
// ============================================

/**
 * Fetch hotel policy rules from the API
 * POST /hotels/policy-rules?sessionId=...&userId=...
 */
export async function fetchHotelPolicyRules(
  params: HotelPolicyRulesRequestParams,
): Promise<HotelPolicyRulesResponse> {
  const emptyData: HotelRulesData = { allRules: [] };

  const payload = buildCBPayload(params.txnKey);

  const { data, error } = await baseFetch<RawResponse>({
    endpoint: HOTELS_ENDPOINTS.POLICY_RULES,
    queryParams: { sessionId: params.sessionId, userId: params.userId },
    body: payload,
    logTag: 'HotelPolicyRulesClient',
  });

  // Network/HTTP error
  if (error) {
    return { type: 'hotels-policy-rules', success: false, data: emptyData, error };
  }

  const rulesData: HotelRulesData = { allRules: data?.allRules ?? [] } as HotelRulesData;

  // API returned error
  if (data?.success === false && data?.error) {
    logger.warn('[HotelPolicyRulesClient] API returned error:', data.error);
    return { type: 'hotels-policy-rules', success: false, data: rulesData, error: data.error };
  }

  // Success
  logger.warn('[HotelPolicyRulesClient] Success:', { rulesCount: rulesData.allRules.length });
  return { type: 'hotels-policy-rules', success: true, data: rulesData };
}
